import React, { useState, useEffect } from 'react';
import { cropAPI } from '../../services/api';

const QRCodeManager = () => {
  const [seeds, setSeeds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState('');

  const [selectedCrop, setSelectedCrop] = useState('');
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
  const [qrOptions, setQrOptions] = useState({
    size: 300,
    format: 'PNG',
    errorLevel: 'M',
    quietZone: 4,
    foregroundColor: '',
    backgroundColor: '',
    includeBranding: false,
    brandText: ''
  });
  const [downloading, setDownloading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

  const selectedCropData = seeds.find(seed => seed.varietyCode === selectedCrop);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setMessage('Please enter a search query');
      return;
    }

    try {
      setSearching(true);
      setMessage('');
      // Hardcoded limit to 1 result
      const response = await cropAPI.searchCrops(searchQuery, 'en', 1, true);
      setSeeds(response.data);
      if (response.data.length === 0) {
        setMessage('No active seed found');
      } else {
        setMessage(`Found: ${response.data[0].varietyCode} - ${response.data[0].cropName}`);
        // Auto-select the found seed
        setSelectedCrop(response.data[0].varietyCode);
      }
    } catch (error) {
      setMessage(`Search failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setSearching(false);
    }
  };

  const handleOptionChange = (key, value) => {
    setQrOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Load QR preview when crop is selected
  useEffect(() => {
    const loadPreview = async () => {
      if (!selectedCrop) {
        setQrPreviewUrl(null);
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `${API_BASE_URL}/QrCode/${encodeURIComponent(selectedCrop)}/preview?size=200&format=PNG`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setQrPreviewUrl(url);
        } else {
          setQrPreviewUrl(null);
        }
      } catch (error) {
        console.error('Failed to load QR preview:', error);
        setQrPreviewUrl(null);
      }
    };

    loadPreview();

    // Cleanup
    return () => {
      if (qrPreviewUrl) {
        URL.revokeObjectURL(qrPreviewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrop, API_BASE_URL]);

  const downloadQRCode = async () => {
    if (!selectedCrop) return;

    try {
      setDownloading(true);
      setMessage('Downloading QR code...');

      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        size: qrOptions.size,
        format: qrOptions.format,
        errorLevel: qrOptions.errorLevel,
        quietZone: qrOptions.quietZone,
        includeBranding: qrOptions.includeBranding
      });

      if (qrOptions.foregroundColor) {
        params.append('foregroundColor', qrOptions.foregroundColor);
      }
      if (qrOptions.backgroundColor) {
        params.append('backgroundColor', qrOptions.backgroundColor);
      }
      if (qrOptions.includeBranding) {
        // Use brandText if provided, otherwise use variety code
        params.append('brandText', qrOptions.brandText || selectedCrop);
      }

      const response = await fetch(
        `${API_BASE_URL}/QrCode/${encodeURIComponent(selectedCrop)}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download QR code');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileExtension = qrOptions.format.toLowerCase();
      link.download = `qr_${selectedCrop}_${qrOptions.size}x${qrOptions.size}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage('QR code downloaded successfully! ✅');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`Failed to download: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const downloadAllQRCodes = async () => {
    try {
      setDownloading(true);
      setMessage(`Downloading all ${qrOptions.format} QR codes...`);

      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        size: qrOptions.size,
        format: qrOptions.format,
        errorLevel: qrOptions.errorLevel,
        quietZone: qrOptions.quietZone,
        includeBranding: qrOptions.includeBranding
      });

      if (qrOptions.foregroundColor) {
        params.append('foregroundColor', qrOptions.foregroundColor);
      }
      if (qrOptions.backgroundColor) {
        params.append('backgroundColor', qrOptions.backgroundColor);
      }
      if (qrOptions.includeBranding) {
        // For bulk download, brandText will be set per variety in backend
        // But we still send the custom text if provided
        if (qrOptions.brandText) {
          params.append('brandText', qrOptions.brandText);
        }
      }

      const response = await fetch(
        `${API_BASE_URL}/QrCode/download-all?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download QR codes');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const formatLower = qrOptions.format.toLowerCase();
      link.download = `qr_codes_all_${formatLower}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage(`All ${qrOptions.format} QR codes downloaded successfully! ✅`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`Failed to download: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="qr-code-manager" style={{ padding: '20px' }}>
      <h1>QR Code Manager</h1>

      {message && (
        <div className={`message ${message.includes('success') || message.includes('✅') ? 'success' : 'error'}`} style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {/* Search Section */}
      <div className="search-section" style={{ marginBottom: '30px', background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2>Search Active Seed</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by crop name or variety code (returns 1 result)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            style={{ flex: 1, padding: '10px', fontSize: '16px', border: '2px solid #e0e0e0', borderRadius: '6px' }}
          />
          <button
            onClick={handleSearch}
            disabled={searching}
            className="upload-button"
            style={{ whiteSpace: 'nowrap' }}
          >
            {searching ? '⏳ Searching...' : '🔍 Search'}
          </button>
        </div>
      </div>

      <div className="qr-content" style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '30px', marginBottom: '30px' }}>
        <div className="qr-controls" style={{ background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          {selectedCropData && (
            <div className="crop-details" style={{ background: '#f8f9fa', padding: '20px', borderRadius: '6px', marginBottom: '20px' }}>
              <h3 style={{ color: '#2d5016', marginBottom: '10px' }}>{selectedCropData.varietyCode}</h3>
              <p><strong>Seed:</strong> {selectedCropData.cropName}</p>
              <p><strong>Status:</strong> {selectedCropData.isActive ? 'Active' : 'Inactive'}</p>
            </div>
          )}

          <div className="qr-options" style={{ marginTop: '30px' }}>
            <h3 style={{ color: '#333', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #e0e0e0' }}>QR Code Options</h3>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div className="form-group">
                <label htmlFor="size">Size (pixels):</label>
                <input
                  type="number"
                  id="size"
                  min="100"
                  max="1000"
                  value={qrOptions.size}
                  onChange={(e) => handleOptionChange('size', parseInt(e.target.value))}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: '6px' }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  {qrOptions.format === 'PNG' ? 'Print-quality PNG format' : 'SVG vector graphics'}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="format">Format:</label>
                <select
                  id="format"
                  value={qrOptions.format}
                  onChange={(e) => handleOptionChange('format', e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: '6px' }}
                >
                  <option value="PNG">PNG</option>
                  <option value="SVG">SVG</option>
                </select>
              </div>
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div className="form-group">
                <label htmlFor="errorLevel">Error Correction:</label>
                <select
                  id="errorLevel"
                  value={qrOptions.errorLevel}
                  onChange={(e) => handleOptionChange('errorLevel', e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: '6px' }}
                >
                  <option value="L">Low (7%)</option>
                  <option value="M">Medium (15%)</option>
                  <option value="Q">Quartile (25%)</option>
                  <option value="H">High (30%)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="quietZone">Quiet Zone:</label>
                <input
                  type="number"
                  id="quietZone"
                  min="0"
                  max="10"
                  value={qrOptions.quietZone}
                  onChange={(e) => handleOptionChange('quietZone', parseInt(e.target.value))}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: '6px' }}
                />
              </div>
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div className="form-group">
                <label htmlFor="foregroundColor">Foreground Color:</label>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <input
                    type="color"
                    id="foregroundColor"
                    value={qrOptions.foregroundColor || '#000000'}
                    onChange={(e) => handleOptionChange('foregroundColor', e.target.value)}
                    style={{ width: '60px', height: '40px', border: '2px solid #e0e0e0', borderRadius: '6px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleOptionChange('foregroundColor', '')}
                    className="clear-color"
                    style={{ flex: 1, padding: '8px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Default
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="backgroundColor">Background Color:</label>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <input
                    type="color"
                    id="backgroundColor"
                    value={qrOptions.backgroundColor || '#ffffff'}
                    onChange={(e) => handleOptionChange('backgroundColor', e.target.value)}
                    style={{ width: '60px', height: '40px', border: '2px solid #e0e0e0', borderRadius: '6px' }}
                  />
                  <button
                    type="button"
                    onClick={() => handleOptionChange('backgroundColor', '')}
                    className="clear-color"
                    style={{ flex: 1, padding: '8px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Default
                  </button>
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input
                  type="checkbox"
                  checked={qrOptions.includeBranding}
                  onChange={(e) => handleOptionChange('includeBranding', e.target.checked)}
                  style={{ margin: 0 }}
                />
                <span>Include branding text below QR code</span>
              </label>

              {qrOptions.includeBranding && (
                <div style={{ marginTop: '10px' }}>
                  <input
                    type="text"
                    placeholder={`Default: ${selectedCrop || 'variety_code'}`}
                    value={qrOptions.brandText}
                    onChange={(e) => handleOptionChange('brandText', e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '14px' }}
                  />
                  <small style={{ color: '#666', fontSize: '11px', display: 'block', marginTop: '5px' }}>
                    Leave empty to use variety code as default
                  </small>
                </div>
              )}
            </div>
          </div>

          {selectedCrop && (
            <div className="qr-actions" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={downloadQRCode}
                disabled={downloading}
                className="download-button primary"
                style={{ width: '100%' }}
              >
                {downloading ? '⏳ Downloading...' : `📥 Download ${qrOptions.format} QR Code`}
              </button>

              <a
                href={`/variety/${encodeURIComponent(selectedCrop)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="download-button secondary"
                style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}
              >
                👁️ View Variety Page
              </a>
            </div>
          )}
        </div>

        <div className="qr-preview" style={{ background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h3 style={{ color: '#333', marginBottom: '20px' }}>Preview</h3>
          {selectedCrop && qrPreviewUrl ? (
            <div className="preview-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
              <img
                src={qrPreviewUrl}
                alt={`QR Code for ${selectedCrop}`}
                style={{ maxWidth: '200px', maxHeight: '200px', border: '1px solid #e0e0e0', borderRadius: '6px' }}
              />
              <div className="preview-info" style={{ textAlign: 'left', color: '#666', fontSize: '14px' }}>
                <p style={{ marginBottom: '5px' }}><strong>Variety:</strong> {selectedCrop}</p>
                <p style={{ marginBottom: '5px' }}><strong>Size:</strong> {qrOptions.size}×{qrOptions.size}px</p>
                <p style={{ marginBottom: '5px' }}><strong>Format:</strong> {qrOptions.format}</p>
                <p style={{ marginBottom: '5px' }}><strong>Error Level:</strong> {qrOptions.errorLevel}</p>
              </div>
            </div>
          ) : (
            <div className="no-preview" style={{ color: '#999', fontStyle: 'italic', padding: '40px' }}>
              <p>Select a variety to see QR code preview</p>
            </div>
          )}
        </div>
      </div>

      <div className="bulk-downloads" style={{ background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3 style={{ color: '#2d5016', marginBottom: '20px' }}>Bulk Download</h3>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
          Download all QR codes with the same settings configured above (size, colors, branding, etc.)
        </p>
        <div className="bulk-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          <button
            onClick={downloadAllQRCodes}
            disabled={downloading || seeds.length === 0}
            className="download-button bulk"
            style={{ opacity: (downloading || seeds.length === 0) ? 0.5 : 1 }}
          >
            {downloading ? '⏳ Downloading...' : `📦 Download All QR Codes (${qrOptions.format} ZIP)`}
          </button>
        </div>
        {seeds.length === 0 && (
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
            Search for seeds first to enable bulk downloads
          </p>
        )}
      </div>
    </div>
  );
};

export default QRCodeManager;
